import type { Control, UseFormSetValue } from "react-hook-form";
import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";

import type { AssessmentElement, AssessmentFormValues } from "@/core/types";

import { adaptCheckboxOptions } from "../helpers/input-adapters";

type ExclusiveOptionHandlerProps = {
  control: Control;
  elements: AssessmentElement[];
  setValue: UseFormSetValue<AssessmentFormValues>;
};

/**
 * Form observer that handles exclusive option behavior in checkbox questions.
 * When an exclusive option is selected, all other options are automatically cleared.
 * When a non-exclusive option is selected while an exclusive option is active, the exclusive option is removed.
 */
export function ExclusiveOptionHandler({
  control,
  elements,
  setValue,
}: ExclusiveOptionHandlerProps): null {
  const exclusiveOptionMap = useRef<Map<string, Set<string>>>(new Map());

  // Initialize the map only once
  useEffect(() => {
    elements.forEach((element) => {
      if (element.type === "checkbox") {
        const options = adaptCheckboxOptions(element.choices);
        const exclusiveValues = options.filter((opt) => opt.isExclusive).map((opt) => opt.value);

        if (exclusiveValues.length > 0) {
          exclusiveOptionMap.current.set(element.name, new Set(exclusiveValues));
        }
      }
    });
  }, [elements]);

  const checkboxFieldNames = elements.filter((el) => el.type === "checkbox").map((el) => el.name);

  const watchedValues = useWatch({
    control,
    name: checkboxFieldNames,
  });

  // Track previous values to detect what changed
  const previousValuesRef = useRef<Record<string, string[]>>({});

  useEffect(() => {
    // Process each checkbox field
    checkboxFieldNames.forEach((fieldName, index) => {
      const currentValues = Array.isArray(watchedValues[index])
        ? (watchedValues[index] as string[])
        : [];

      const previousValues = previousValuesRef.current[fieldName] || [];
      const exclusiveOptions = exclusiveOptionMap.current.get(fieldName);

      // Skip if no exclusive options defined for this field
      if (!exclusiveOptions || exclusiveOptions.size === 0) {
        previousValuesRef.current[fieldName] = currentValues;

        return;
      }

      // Skip if values haven't changed
      if (
        currentValues.length === previousValues.length &&
        currentValues.every((val) => previousValues.includes(val))
      ) {
        return;
      }

      // Find newly added values
      const addedValues = currentValues.filter((val) => !previousValues.includes(val));

      if (addedValues.length > 0) {
        // Check if any newly added value is exclusive
        const addedExclusive = addedValues.find((val) => exclusiveOptions.has(val));

        if (addedExclusive) {
          // An exclusive option was selected - keep only that option
          setValue(fieldName, [addedExclusive], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });

          // Update our tracking
          previousValuesRef.current[fieldName] = [addedExclusive];

          return;
        }

        // A non-exclusive option was added - check if exclusive options exist in current values
        const hasExclusiveSelected = currentValues.some((val) => exclusiveOptions.has(val));

        if (hasExclusiveSelected) {
          // Remove all exclusive options, keep only non-exclusive ones
          const nonExclusiveValues = currentValues.filter((val) => !exclusiveOptions.has(val));

          setValue(fieldName, nonExclusiveValues, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });

          // Update our tracking
          previousValuesRef.current[fieldName] = nonExclusiveValues;

          return;
        }
      }

      // Update tracking for next comparison
      previousValuesRef.current[fieldName] = currentValues;
    });
  }, [watchedValues, checkboxFieldNames, control, setValue]);

  return null;
}
